import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, SearchField } from '../kit';

const SEARCH_AT = 8; // show the filter once the list stops fitting at a glance

/**
 * Checkbox list of an org's locations for location roles.
 *   click          toggle one
 *   click + drag   paint: every row the pointer crosses takes the state the
 *                  first row was switched to (check or uncheck)
 *   shift + click  apply that state to the whole range since the last click
 * Keyboard: Tab to a box, Space toggles.
 *
 * ids the org list doesn't know about (deleted locations) are kept as-is.
 */
export default function LocationChecklist({ locations, value, onChange, disabled }) {
  const [query, setQuery] = useState('');
  const paint = useRef(null); // { checked, ids: Set } while dragging
  const anchor = useRef(null); // index of the last clicked row, for shift-click
  const valueRef = useRef(value);
  valueRef.current = value;
  // The drag listener is bound once; always call the parent's latest
  // onChange — an old one spreads an old org entry back over newer edits.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const selected = useMemo(() => new Set(value.map(String)), [value]);
  const sorted = useMemo(
    () => [...locations].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [locations]
  );
  const q = query.trim().toLowerCase();
  const visible = q
    ? sorted.filter((l) => String(l.name || '').toLowerCase().includes(q) || String(l.id) === q)
    : sorted;

  // Returns the next id list with `ids` set to `checked`, preserving order.
  const apply = (ids, checked) => {
    const current = valueRef.current;
    const keys = new Set(ids.map(String));
    if (!checked) return current.filter((x) => !keys.has(String(x)));
    const have = new Set(current.map(String));
    return [...current, ...ids.filter((id) => !have.has(String(id)))];
  };
  const set = (ids, checked) => {
    const next = apply(ids, checked);
    valueRef.current = next; // a fast drag can fire again before the re-render
    onChangeRef.current(next);
  };

  useEffect(() => {
    const end = () => { paint.current = null; };
    const onMove = (e) => {
      const p = paint.current;
      if (!p) return;
      const row = document.elementFromPoint(e.clientX, e.clientY)?.closest?.('[data-loc-id]');
      const id = row?.getAttribute('data-loc-id');
      if (!id || p.ids.has(id)) return;
      p.ids.add(id);
      const loc = locations.find((l) => String(l.id) === id);
      if (loc) set([loc.id], p.checked);
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    window.addEventListener('pointermove', onMove);
    return () => {
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      window.removeEventListener('pointermove', onMove);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  const onRowPointerDown = (e, loc, index) => {
    if (disabled || e.button !== 0) return;
    e.preventDefault(); // no text selection / focus steal while painting
    const checked = !selected.has(String(loc.id));
    if (e.shiftKey && anchor.current != null && visible[anchor.current]) {
      const [a, b] = [anchor.current, index].sort((x, y) => x - y);
      set(visible.slice(a, b + 1).map((l) => l.id), checked);
    } else {
      set([loc.id], checked);
    }
    anchor.current = index;
    if (e.pointerType === 'mouse') paint.current = { checked, ids: new Set([String(loc.id)]) };
  };

  const visibleIds = visible.map((l) => l.id);
  const allVisibleOn = visible.length > 0 && visible.every((l) => selected.has(String(l.id)));
  const known = locations.filter((l) => selected.has(String(l.id))).length;

  return (
    <div className="us-dept-editor us-loc-editor">
      <div className="us-dept-editor-head">
        <span>
          <strong>{known}</strong> of {locations.length} selected
        </span>
        <span className="us-loc-grow" />
        <Button size="small" variant="ghost" disabled={disabled || allVisibleOn} onClick={() => set(visibleIds, true)}>
          {q ? 'Select matches' : 'Select all'}
        </Button>
        <Button size="small" variant="ghost" disabled={disabled || !value.length} onClick={() => set(q ? visibleIds : value, false)}>
          {q ? 'Clear matches' : 'Clear'}
        </Button>
      </div>
      {locations.length > SEARCH_AT ? (
        <div className="us-loc-search">
          <SearchField value={query} onChange={setQuery} placeholder="Filter locations" label="Filter locations" />
        </div>
      ) : null}
      <div className="us-checklist us-loc-list" role="group" aria-label="Locations">
        {visible.map((l, i) => {
          const on = selected.has(String(l.id));
          return (
            <label
              key={l.id}
              data-loc-id={String(l.id)}
              className={`us-check-row us-loc-row${on ? ' is-on' : ''}`}
              onPointerDown={(e) => onRowPointerDown(e, l, i)}
              onClick={(e) => { if (e.detail > 0) e.preventDefault(); }} // pointer handled on pointerdown
            >
              <input
                type="checkbox"
                className="ak-check"
                checked={on}
                disabled={disabled}
                onChange={(e) => { anchor.current = i; set([l.id], e.target.checked); }}
              />
              <span className="us-loc-name">{l.name || `Location ${l.id}`}</span>
              <span className="ak-mono ak-muted us-loc-id">#{l.id}</span>
            </label>
          );
        })}
        {!visible.length ? (
          <span className="ak-help">{locations.length ? `No location matches “${query}”.` : 'This organization has no locations.'}</span>
        ) : null}
      </div>
    </div>
  );
}
